import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { apiConfig, getApiUrl } from 'src/config/apiConfig';
import axiosIn from 'src/middlewares/axiosInterceptor';
import { NotificationPreference } from 'src/types/notification';
import { extractErrorMsg } from 'src/utils/utils';

interface NotificationPreferencesState {
  preferences: NotificationPreference[];
  loading: boolean;
  error: string | null;
}

const initialState: NotificationPreferencesState = {
  preferences: [],
  loading: false,
  error: null,
};

// Async thunks for CRUD operations
export const fetchNotificationPreferences = createAsyncThunk(
  'notificationPreferences/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosIn.get(getApiUrl(apiConfig.account_notification_get_preferences));
      return response.data;
    } catch (error: any) {
      return rejectWithValue(extractErrorMsg(error));
    }
  }
);

export const createNotificationPreference = createAsyncThunk(
  'notificationPreferences/create',
  async (data: Omit<NotificationPreference, 'id' | 'userId'>, { rejectWithValue }) => {
    try {
      const response = await axiosIn.post(getApiUrl(apiConfig.account_notification_get_preferences), data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(extractErrorMsg(error));
    }
  }
);

export const updateNotificationPreference = createAsyncThunk(
  'notificationPreferences/update',
  async ({ id, ...data }: NotificationPreference, { rejectWithValue }) => {
    try {
      const response = await axiosIn.put(getApiUrl(apiConfig.account_notification_patchpost_preference), { id, ...data });
      return response.data;
    } catch (error: any) {
      return rejectWithValue(extractErrorMsg(error));
    }
  }
);

export const deleteNotificationPreference = createAsyncThunk(
  'notificationPreferences/delete',
  async (id: string, { rejectWithValue }) => {
    try {
      await axiosIn.delete(getApiUrl(apiConfig.account_notification_delete_preference, { id }));
      return id;
    } catch (error: any) {
      return rejectWithValue(extractErrorMsg(error));
    }
  }
);

const notificationPreferencesSlice = createSlice({
  name: 'notificationPreferences',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all preferences
      .addCase(fetchNotificationPreferences.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNotificationPreferences.fulfilled, (state, action) => {
        state.loading = false;
        state.preferences = action.payload;
      })
      .addCase(fetchNotificationPreferences.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Create preference
      .addCase(createNotificationPreference.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createNotificationPreference.fulfilled, (state, action) => {
        state.loading = false;
        state.preferences.push(action.payload);
      })
      .addCase(createNotificationPreference.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Update preference
      .addCase(updateNotificationPreference.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateNotificationPreference.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.preferences.findIndex(p => p.id === action.payload.id);
        if (index !== -1) {
          state.preferences[index] = action.payload;
        }
      })
      .addCase(updateNotificationPreference.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Delete preference
      .addCase(deleteNotificationPreference.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteNotificationPreference.fulfilled, (state, action) => {
        state.loading = false;
        state.preferences = state.preferences.filter(p => p.id !== action.payload);
      })
      .addCase(deleteNotificationPreference.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError } = notificationPreferencesSlice.actions;
export default notificationPreferencesSlice.reducer; 